import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { fetchWidgetConfig } from "@/widget/api";
import { ShadowMount } from "@/widget/WidgetShadowMount";
import { WidgetShell } from "@/widget/WidgetShell";
import { WidgetErrorBoundary } from "@/widget/WidgetErrorBoundary";
import type { WidgetConfigData } from "@/widget/types";

/**
 * DEV-ONLY harness for iterating on the widget with hot-reload. Fetches a widget's
 * real config from the backend, lets you OVERRIDE any presentation field on top of
 * it (display type, trigger button, colours, size, status), then mounts the widget
 * from source into a REAL shadow root via a React portal — so editing anything in
 * `src/widget/` hot-reloads here instantly while keeping the true shadow environment.
 * Not for production.
 *
 * Open http://localhost:5173/widget-dev
 */
export const Route = createFileRoute("/widget-dev")({
  component: WidgetDevPage,
});

function OverrideSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
      <span style={{ color: "#667" }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: 6, minWidth: 150 }}
      >
        <option value="">(from widget)</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// Config keys the harness can override (all string-valued presentation fields).
type OverrideKey =
  | "display_type"
  | "trigger_button"
  | "widget_button_colour"
  | "widget_button_colour_theme"
  | "size"
  | "dark_mode";

function WidgetDevPage() {
  const [widgetId, setWidgetId] = useState("116");
  const [base, setBase] = useState<WidgetConfigData | null>(null);
  const [baseStatus, setBaseStatus] = useState<"active" | "inactive">("active");
  const [loadedId, setLoadedId] = useState("116");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [ov, setOv] = useState<Partial<Record<OverrideKey, string>>>({});
  const [statusOv, setStatusOv] = useState("");

  const load = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWidgetConfig(id);
      if (res.data && (res.status === "active" || res.status === "inactive")) {
        setBase(res.data);
        setBaseStatus(res.status);
        setLoadedId(id);
      } else {
        setBase(null);
        setError(`config status: ${res.status}`);
      }
    } catch (e) {
      setBase(null);
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load("116");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOverride = (k: OverrideKey, v: string) =>
    setOv((prev) => {
      const next = { ...prev };
      if (v) next[k] = v;
      else delete next[k];
      return next;
    });

  if (!import.meta.env.DEV) {
    return <div style={{ padding: 24 }}>Not available in production.</div>;
  }

  const effective = base ? ({ ...base, ...ov } as WidgetConfigData) : null;
  const status: "active" | "inactive" =
    statusOv === "active" || statusOv === "inactive" ? statusOv : baseStatus;
  const dark = (effective?.dark_mode ?? "OFF") === "ON";

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <h1 style={{ margin: "0 0 4px" }}>Widget dev harness (HMR)</h1>
      <p style={{ marginTop: 0, color: "#667" }}>
        Loads a widget's real config from localbackend, then overrides presentation
        fields on top. Edits in <code>src/widget/</code> hot-reload here, inside a real
        shadow root.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
          <span style={{ color: "#667" }}>widget_id</span>
          <input
            value={widgetId}
            onChange={(e) => setWidgetId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void load(widgetId)}
            style={{ padding: 6, width: 120 }}
          />
        </label>
        <button onClick={() => void load(widgetId)} style={{ padding: "8px 14px" }}>
          Load
        </button>
        {loading ? <span style={{ color: "#667" }}>loading…</span> : null}
        {error ? <span style={{ color: "#b00" }}>{error}</span> : null}
      </div>

      {/* Override controls */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 16,
          padding: 12,
          border: "1px solid #e2e2e5",
          borderRadius: 8,
          background: "#fafafa",
        }}
      >
        <OverrideSelect
          label="display_type"
          value={ov.display_type ?? ""}
          onChange={(v) => setOverride("display_type", v)}
          options={[
            { value: "Embedded Inline", label: "Embedded Inline" },
            { value: "Button Dropdown", label: "Button Dropdown" },
            { value: "Modal Overlay", label: "Modal Overlay" },
          ]}
        />
        <OverrideSelect
          label="trigger_button"
          value={ov.trigger_button ?? ""}
          onChange={(v) => setOverride("trigger_button", v)}
          options={[
            { value: "Border Button", label: "Border Button" },
            { value: "Filled Button", label: "Filled Button" },
            { value: "Banner", label: "Banner" },
            { value: "No Trigger", label: "No Trigger" },
          ]}
        />
        <OverrideSelect
          label="widget_button_colour"
          value={ov.widget_button_colour ?? ""}
          onChange={(v) => setOverride("widget_button_colour", v)}
          options={[
            { value: "Standard Theme", label: "Standard" },
            { value: "Neutral Theme", label: "Neutral" },
            { value: "Blue Theme", label: "Blue" },
          ]}
        />
        <OverrideSelect
          label="button theme"
          value={ov.widget_button_colour_theme ?? ""}
          onChange={(v) => setOverride("widget_button_colour_theme", v)}
          options={[
            { value: "OFF", label: "Light" },
            { value: "ON", label: "Dark" },
          ]}
        />
        <OverrideSelect
          label="size"
          value={ov.size ?? ""}
          onChange={(v) => setOverride("size", v)}
          options={[
            { value: "Small", label: "Small (375)" },
            { value: "Standard", label: "Standard (560)" },
            { value: "Custom", label: "Custom" },
          ]}
        />
        <OverrideSelect
          label="dark_mode (content)"
          value={ov.dark_mode ?? ""}
          onChange={(v) => setOverride("dark_mode", v)}
          options={[
            { value: "OFF", label: "Light" },
            { value: "ON", label: "Dark" },
          ]}
        />
        <OverrideSelect
          label="status"
          value={statusOv}
          onChange={setStatusOv}
          options={[
            { value: "active", label: "active" },
            { value: "inactive", label: "inactive" },
          ]}
        />
        <button
          onClick={() => {
            setOv({});
            setStatusOv("");
          }}
          style={{ alignSelf: "flex-end", padding: "8px 14px", height: 34 }}
        >
          Reset overrides
        </button>
      </div>

      <div
        style={{
          marginTop: 16,
          padding: 16,
          border: "1px dashed #bcc",
          borderRadius: 8,
          background: dark ? "#111" : "#fff",
        }}
      >
        {effective ? (
          /* key by loadedId → fresh shadow root per widget; overrides update live */
          <ShadowMount key={loadedId}>
            <WidgetErrorBoundary>
              <WidgetShell config={effective} status={status} />
            </WidgetErrorBoundary>
          </ShadowMount>
        ) : (
          <span style={{ color: "#667" }}>No config loaded.</span>
        )}
      </div>
    </div>
  );
}
